import type { ObjectId } from 'mongodb';

import type { AccountDocument } from './AccountTypes';
import { DocumentTypes } from './DocumentTypes';
import { FlagPurposeType, FlagReason, FlagStatus } from './Flags';
import type { Flag, FlagActionHistory } from './Flags';
import type { WithDatesAsIsoStrings } from './Helpers';
import { MilestonePackageRoute, MilestoneType } from './Milestones';
import { PayoutEvent } from './Payouts/Events';
import type { ReferenceIdObject } from './ReferenceIds';
import type { PackageRejectionInfo } from './RejectionReasons';
import { RequirementFlagSettingsType } from './RequirementFlagSettings';
import type { UserInfo, UserWithDate } from './UserTypes';

export enum QueueStatus {
  inProgress = 'inProgress',
  reReview = 'reReview',
  rejected = 'rejected',
  resubmitted = 'resubmitted',
  submitted = 'submitted',
  onHold = 'onHold',
  completeHoldForApproval = 'completeHoldForApproval',
  approved = 'approved',
  conditionallyApproved = 'conditionallyApproved',
}

export const UNDER_REVIEW_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.inProgress,
  QueueStatus.reReview,
]);
export const PACKAGE_EDITABLE_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.rejected,
  QueueStatus.submitted,
  QueueStatus.resubmitted,
]);
export const PACKAGE_NOT_EDITABLE_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.inProgress,
  QueueStatus.reReview,
  QueueStatus.onHold,
  QueueStatus.completeHoldForApproval,
]);
export const APPROVED_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.approved,
  QueueStatus.conditionallyApproved,
]);
export const PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.submitted,
  QueueStatus.inProgress,
  QueueStatus.reReview,
]);
export const NON_SPECIAL_PACKAGE_REVIEWED_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.rejected,
  QueueStatus.approved,
  QueueStatus.conditionallyApproved,
]);
export const CLAIMABLE_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.inProgress,
  QueueStatus.reReview,
  QueueStatus.resubmitted,
  QueueStatus.submitted,
  QueueStatus.onHold,
  QueueStatus.rejected,
]);
export const ACTIVE_QUEUE_STATUSES: Set<QueueStatus> = new Set<QueueStatus>([
  QueueStatus.submitted,
  QueueStatus.inProgress,
  QueueStatus.reReview,
  QueueStatus.resubmitted,
]);

export enum QueueHistoryAction {
  status = 'status',
  claimedBy = 'claimedBy',
  flag = 'flag',
}

export enum QueueType {
  activationPackage = 'activationPackage',
  disclosureDocument = 'disclosureDocument',
  installPackage = 'installPackage',
  voidedCheck = 'voidedCheck',
  identityVerification = 'identityVerification',
  permissionToOperate = 'permissionToOperate',
  supportingDocument = 'supportingDocument',
  titleVerification = 'titleVerification',
  electricianSignOff = 'electricianSignOff',
  postActivationPackage = 'postActivationPackage',
  domesticContent = 'domesticContent',
  siteAssessment = 'siteAssessment',
  warrantyRegistration = 'warrantyRegistration',
  uploadedContract = 'uploadedContract',
  utilityBill = 'utilityBill',
  holdMotion = 'holdMotion',
  loopFieldPackage = 'loopFieldPackage',
  permit = 'permit',
  loopFieldDesignDocuments = 'loopFieldDesignDocuments',
  welcomeCall = 'welcomeCall',
}

export enum QueueTypeLabel {
  activationPackage = 'Activation Package',
  disclosureDocument = 'Disclosure Document',
  installPackage = 'Install Package',
  voidedCheck = 'Voided Check',
  identityVerification = 'Identity Verification',
  permissionToOperate = 'Permission to Operate',
  supportingDocument = 'Supporting Document',
  titleVerification = 'Title Verification',
  electricianSignOff = 'Electrician Sign Off',
  postActivationPackage = 'Post Activation Package',
  domesticContent = 'Domestic Content',
  siteAssessment = 'Site Assessment',
  warrantyRegistration = 'Warranty Registration',
  uploadedContract = 'Uploaded Contract',
  utilityBill = 'Utility Bill',
  holdMotion = 'Hold Motion',
  loopFieldPackage = 'Loop Field Package',
  permit = 'Permit',
  loopFieldDesignDocuments = 'GSHP Design Documents',
  welcomeCall = 'Welcome Call Review',
}

export const queueTypeLabelMap: Record<QueueType, QueueTypeLabel> = {
  [QueueType.activationPackage]: QueueTypeLabel.activationPackage,
  [QueueType.disclosureDocument]: QueueTypeLabel.disclosureDocument,
  [QueueType.installPackage]: QueueTypeLabel.installPackage,
  [QueueType.voidedCheck]: QueueTypeLabel.voidedCheck,
  [QueueType.identityVerification]: QueueTypeLabel.identityVerification,
  [QueueType.permissionToOperate]: QueueTypeLabel.permissionToOperate,
  [QueueType.supportingDocument]: QueueTypeLabel.supportingDocument,
  [QueueType.titleVerification]: QueueTypeLabel.titleVerification,
  [QueueType.electricianSignOff]: QueueTypeLabel.electricianSignOff,
  [QueueType.postActivationPackage]: QueueTypeLabel.postActivationPackage,
  [QueueType.domesticContent]: QueueTypeLabel.domesticContent,
  [QueueType.siteAssessment]: QueueTypeLabel.siteAssessment,
  [QueueType.warrantyRegistration]: QueueTypeLabel.warrantyRegistration,
  [QueueType.uploadedContract]: QueueTypeLabel.uploadedContract,
  [QueueType.utilityBill]: QueueTypeLabel.utilityBill,
  [QueueType.holdMotion]: QueueTypeLabel.holdMotion,
  [QueueType.loopFieldPackage]: QueueTypeLabel.loopFieldPackage,
  [QueueType.permit]: QueueTypeLabel.permit,
  [QueueType.loopFieldDesignDocuments]: QueueTypeLabel.loopFieldDesignDocuments,
  [QueueType.welcomeCall]: QueueTypeLabel.welcomeCall,
};

export interface OpenFlagsOnReview {
  id?: ObjectId;
  dataKey?: string;
  flagType?: RequirementFlagSettingsType;
  purposeType?: FlagPurposeType;
}

export interface QueueHistory extends UserWithDate {
  id?: ObjectId;
  action: QueueHistoryAction;
  changedFrom: any;
  changedTo: any;
  purposeType?: FlagPurposeType;
  status?: FlagStatus;
  note?: string;
  dataKey?: string;
  fileName?: string;
  reasons?: (FlagReason | QueueHoldReason | QueueRejectionReason)[];
  openFlags?: OpenFlagsOnReview[];
  flagAction?: Pick<FlagActionHistory, 'flagSubject' | 'actionStatus'>;
}

export enum QueueHoldReason {
  pendingException = 'Pending Exception',
  pendingFurtherInternalReview = 'Pending Further Internal Review',
  pendingMonitoringValidation = 'Pending Monitoring Validation',
  pendingWarrantyInfo = 'Pending Warranty Info',
}

export interface QueueHoldReasons extends UserWithDate {
  reasons: QueueHoldReason[];
  note?: string;
}

export interface QueueNote extends UserWithDate {
  note: string;
}

export enum QueueRejectionReason {
  notComplete = 'Not Complete',
  missingDocumentation = 'Missing Documentation',
  illegibleDocumentation = 'Illegible Documentation',
  incorrectDocumentation = 'Incorrect Documentation',
  other = 'Other',
  codeViolation = 'Code Violation',
}

export interface QueueRejectionReasons extends UserWithDate {
  reasons: QueueRejectionReason[];
  rejectedDocumentId?: ObjectId;
  note?: string;
}

export enum QueueAssigneeType {
  internal = 'internal',
}

export enum AIPackageReviewStatus {
  completed = 'completed',
  completedWithFailures = 'completedWithFailures',
  failed = 'failed',
  pending = 'pending',
  processing = 'processing',
}

export interface QueueAttributes {
  hasSubsidyTariff?: boolean;
}

// A single review-SLA clock. Each submission and resubmission starts its own cycle with a fresh
// target computed from that cycle's timestamp — time never carries over between cycles, and a
// prior cycle is never mutated when a new one begins.
export interface ReviewSlaCycle {
  milestone: MilestoneType;
  // false = first submission of this package, true = a resubmission after a decision
  isResubmission: boolean;
  // Snapshot of the SLA duration applied when the target was stamped
  slaBusinessHours: number;
  // Snapshot of the IANA timezone from operating hours settings at the time the target was stamped
  timezone: string;
  // Submission/resubmission timestamp the target was computed from
  startDate: Date;
  // Computed Review SLA target (UTC)
  targetDate: Date;
  decisionDate?: Date;
  decisionStatus?: QueueStatus.approved | QueueStatus.conditionallyApproved | QueueStatus.rejected;
  // true when decisionDate <= targetDate
  metTargetDate?: boolean;
}

// Only install and activation packages carry a review-SLA clock. Other queue types have no entry and
// are skipped when stamping reviewSla.
export const QueueTypeToReviewSlaMilestone: Partial<Record<QueueType, MilestoneType>> = {
  [QueueType.installPackage]: MilestoneType.install,
  [QueueType.activationPackage]: MilestoneType.activation,
};

export type QueueDocument = {
  id: ObjectId;
  assignee?: UserInfo;
  assigneeType?: QueueAssigneeType;
  history?: QueueHistory[];
  holdReasons?: QueueHoldReasons[];
  notes?: QueueNote[];
  approvalNotes?: QueueNote[];
  rejectionReasons?: QueueRejectionReasons[];
  flags?: Flag[];
  status: QueueStatus;
  statusBeforeHold?: QueueStatus;
  name: string;
  description: string;
  referenceIds?: ReferenceIdObject[];
  type: QueueType;
  account: Pick<
    AccountDocument & { systemsTotal?: number },
    | 'id'
    | 'organizationId'
    | 'licensedOrganizationId'
    | 'primaryApplicantName'
    | 'cancellation'
    | 'address'
    | 'externalReference'
    | 'salesRepName'
    | 'applicants'
    | 'programType'
    | 'systemsTotal'
    | 'domesticContentEligibilityStatus'
  > & {
    organizationName: string;
    licensedOrganizationName: string;
    tranches?: string[];
    funds?: string[];
    payeeContractor?: {
      name: string;
      alias: string;
    };
    isPayAtApproval?: boolean;
  };
  attributes?: QueueAttributes;
  createdAt: Date;
  dueDate?: Date;
  updatedAt: Date; // will be the same as createdAt when initially submitted
  lastSubmittedAt: Date; // will be the same as createdAt when initially submitted
  reviewTeam?: {
    id: string;
    name: string;
  };
  exception?: { note: string };
  aiPackageReviewStatus?: AIPackageReviewStatus;
  // Written only on transition into completeHoldForApproval.
  placedOnHoldAt?: Date;
  // QC priority review-SLA tracking (feature-flagged: enable-qc-priority).
  reviewSla?: {
    // Most-recent cycle; sort/filter queues on reviewSla.current.targetDate.
    current: ReviewSlaCycle;
    priorCycles?: ReviewSlaCycle[];
    // Snapshot of the org's current reviewPriorityTier so queues can be sorted by priority in
    // Mongo. Lower = higher priority.
    reviewPriorityTier?: number;
  };
};

export type QueueDocumentResponse = WithDatesAsIsoStrings<QueueDocument>;

export type UpdateQueue = {
  assigneeId?: string | null;
  status?: QueueStatus;
  statusBeforeHold?: QueueStatus;
  rejectionReasons?: {
    reasons: QueueRejectionReason[];
    rejectedDocumentId?: ObjectId;
    note?: string;
    rejectionInfo?: PackageRejectionInfo[];
  }[];
  notes?: {
    note: string;
  }[];
  // A bare string, not a `{ note }` object: the request schema declares `type: 'string'`, both app
  // callers send a trimmed string, and `updateQueue` uses the value directly as a `QueueNote.note`.
  // Do not confuse this write-model field with the persisted `approvalNotes: QueueNote[]`.
  approvalNote?: string;
  holdReasons?: {
    reasons: QueueHoldReason[];
    note?: string;
  }[];
  flags?: {
    reasons: string[];
    note?: string;
    dataKey: string;
  }[];
  exception?: {
    note: string;
  };
  referenceIds?: ReferenceIdObject[];
};

export type ApprovalExceptionNote = {
  note: string;
  exceptionGivenBy: string;
};

export enum SearchQueuesSort {
  MostRelevant = 'MOST_RELEVANT',
  Newest = 'NEWEST',
  Oldest = 'OLDEST',
  NewestUpdates = 'NEWEST_UPDATES',
  OldestUpdates = 'OLDEST_UPDATES',
  NewestSubmitted = 'NEWEST_SUBMITTED',
  OldestSubmitted = 'OLDEST_SUBMITTED',
  OldestOnHold = 'OLDEST_ON_HOLD',
  NewestOnHold = 'NEWEST_ON_HOLD',
  // QC priority (enable-qc-priority): sort on reviewSla.current.targetDate. Packages without a
  // target time always sort last, regardless of direction.
  TargetReviewTimeAsc = 'TARGET_REVIEW_TIME_ASC',
  TargetReviewTimeDesc = 'TARGET_REVIEW_TIME_DESC',
}

export const queueStatusMap: Record<QueueStatus, string> = {
  submitted: 'Submitted',
  resubmitted: 'Resubmitted',
  onHold: 'On Hold',
  inProgress: 'Under Review',
  reReview: 'Re-Review',
  rejected: 'Rejected',
  completeHoldForApproval: 'Review Complete Hold for Approval',
  approved: 'Approved',
  conditionallyApproved: 'Conditionally Approved',
};

export enum ReviewQueueRoute {
  activationPackage = 'activation-package',
  postActivationPackage = 'post-activation-package',
  installPackage = 'install-package',
  voidedCheck = 'voided-check',
  reviewDisclosure = 'review-disclosure',
  reviewIdentity = 'review-identity',
  reviewSupporting = 'review-supporting',
  reviewTitle = 'review-title',
  reviewUploadedDocument = 'uploaded-document',
  reviewPermissionToOperate = 'pto-package',
  electricianSignOff = 'electrician-sign-off',
  domesticContent = 'domestic-content',
  siteAssessment = 'site-assessment',
  warrantyRegistration = 'warranty-registration',
  reviewUtilityBill = 'review-utility-bill',
  holdMotion = 'hold-motion',
  loopFieldPackage = 'loop-field-package',
  permit = 'permit',
  loopFieldDesignDocuments = 'loop-field-design-documents',
  welcomeCall = 'welcome-call',
}

export enum LegacyReviewQueueType {
  title = 'title',
  identity = 'identity',
  uploadedDocument = 'uploaded-document',
  install = 'install',
  systemActivation = 'system-activation',
  voidedCheck = 'voided-check',
}

/** These should be the dataKeys of columns defined in ExtraColumns.tsx */
export enum ExtraQueueColumn {
  dueDate = 'dueDate',
}

export const MilestoneRouteToQueueTypeMap: Record<MilestonePackageRoute, QueueType> = {
  [MilestonePackageRoute.installPackage]: QueueType.installPackage,
  [MilestonePackageRoute.activationPackage]: QueueType.activationPackage,
  [MilestonePackageRoute.postActivationPackage]: QueueType.postActivationPackage,
  [MilestonePackageRoute.loopFieldPackage]: QueueType.loopFieldPackage,
};

export const paramToQueueTypeMap: Record<
  string,
  {
    documentType?: DocumentTypes;
    label: string;
    legacyType?: LegacyReviewQueueType;
    type: QueueType;
    getAccountReviewLink: (accountId: string, queueType?: QueueType) => string;
    extraColumns?: ExtraQueueColumn[];
  }
> = {
  [ReviewQueueRoute.activationPackage]: {
    label: 'Activation Package',
    legacyType: LegacyReviewQueueType.systemActivation,
    type: QueueType.activationPackage,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/activation-package/review`,
  },
  [ReviewQueueRoute.postActivationPackage]: {
    label: 'Post Activation Package',
    type: QueueType.postActivationPackage,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/post-activation-package/review`,
  },
  [ReviewQueueRoute.reviewDisclosure]: {
    documentType: DocumentTypes.disclosure,
    label: 'Disclosure Document',
    legacyType: LegacyReviewQueueType.uploadedDocument,
    type: QueueType.disclosureDocument,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}?scrollTo=documents`,
  },
  [ReviewQueueRoute.installPackage]: {
    label: 'Install Package',
    legacyType: LegacyReviewQueueType.install,
    type: QueueType.installPackage,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/installation-package/review`,
  },
  [ReviewQueueRoute.reviewIdentity]: {
    label: 'Identity Verification',
    legacyType: LegacyReviewQueueType.identity,
    type: QueueType.identityVerification,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}`,
  },
  [ReviewQueueRoute.reviewTitle]: {
    label: 'Title Verification',
    legacyType: LegacyReviewQueueType.title,
    type: QueueType.titleVerification,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}`,
  },
  [ReviewQueueRoute.reviewSupporting]: {
    documentType: DocumentTypes.supporting,
    label: 'Supporting Document',
    legacyType: LegacyReviewQueueType.uploadedDocument,
    type: QueueType.supportingDocument,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}?scrollTo=documents`,
  },
  [ReviewQueueRoute.voidedCheck]: {
    label: 'Voided Check',
    type: QueueType.voidedCheck,
    legacyType: LegacyReviewQueueType.voidedCheck,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}?scrollTo=documents`,
  },
  [ReviewQueueRoute.siteAssessment]: {
    label: 'Site Assessment',
    type: QueueType.siteAssessment,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}?scrollTo=documents`,
  },
  [ReviewQueueRoute.reviewPermissionToOperate]: {
    label: 'Permission to Operate',
    type: QueueType.permissionToOperate,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/proof-of-pto/review`,
  },
  [ReviewQueueRoute.electricianSignOff]: {
    label: 'Electrician Sign Off',
    type: QueueType.electricianSignOff,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/activation-package/review`,
  },
  [ReviewQueueRoute.domesticContent]: {
    label: 'Domestic Content',
    type: QueueType.domesticContent,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/installation-package/review`,
  },
  [ReviewQueueRoute.warrantyRegistration]: {
    label: 'Warranty Registration',
    type: QueueType.warrantyRegistration,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/warranties`,
    extraColumns: [ExtraQueueColumn.dueDate],
  },
  [ReviewQueueRoute.reviewUtilityBill]: {
    documentType: DocumentTypes.utilityBill,
    label: 'Utility Bill',
    type: QueueType.utilityBill,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}?scrollTo=documents`,
  },
  [ReviewQueueRoute.holdMotion]: {
    label: 'Hold',
    type: QueueType.holdMotion,
    getAccountReviewLink: (accountId: string, queueType?: QueueType) => {
      const route =
        (queueType && isHoldMotionQueueType(queueType) ? HoldMotionQueueConfig[queueType].route : undefined) ??
        ReviewQueueRoute.activationPackage;
      return paramToQueueTypeMap[route].getAccountReviewLink(accountId);
    },
  },
  [ReviewQueueRoute.loopFieldPackage]: {
    label: 'Loop Field Package',
    type: QueueType.loopFieldPackage,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/loop-field-package/review`,
  },
  [ReviewQueueRoute.permit]: {
    label: 'Geothermal Permit',
    type: QueueType.permit,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/geothermal-permits/review`,
  },
  [ReviewQueueRoute.loopFieldDesignDocuments]: {
    label: 'GSHP Design Documents',
    type: QueueType.loopFieldDesignDocuments,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}/loop-field-design-documents/review`,
  },
  [ReviewQueueRoute.welcomeCall]: {
    label: 'Welcome Call Review',
    type: QueueType.welcomeCall,
    getAccountReviewLink: (accountId: string) => `/accounts/${accountId}`,
  },
};

export type HoldMotionQueueType =
  | QueueType.installPackage
  | QueueType.activationPackage
  | QueueType.loopFieldPackage
  | QueueType.permissionToOperate;

const HoldMotionQueueConfig: Record<HoldMotionQueueType, { route: ReviewQueueRoute; payoutEvent: PayoutEvent }> = {
  [QueueType.installPackage]: { route: ReviewQueueRoute.installPackage, payoutEvent: PayoutEvent.installApproved },
  [QueueType.activationPackage]: {
    route: ReviewQueueRoute.activationPackage,
    payoutEvent: PayoutEvent.activationApproved,
  },
  [QueueType.loopFieldPackage]: {
    route: ReviewQueueRoute.loopFieldPackage,
    payoutEvent: PayoutEvent.loopFieldApproved,
  },
  [QueueType.permissionToOperate]: {
    route: ReviewQueueRoute.reviewPermissionToOperate,
    payoutEvent: PayoutEvent.ptoApproved,
  },
};

export const HoldMotionDependentQueueTypes: QueueType[] = Object.keys(
  HoldMotionQueueConfig,
) as HoldMotionQueueType[];

export const isHoldMotionQueueType = (queueType: QueueType): queueType is HoldMotionQueueType =>
  queueType in HoldMotionQueueConfig;

// Expands the`holdMotion` queue type to its real dependent types; any other type passes through unchanged.
export const expandHoldMotionQueueType = (queueType: QueueType): QueueType[] =>
  queueType === QueueType.holdMotion ? [...HoldMotionDependentQueueTypes] : [queueType];

export const QueueTypeHeldByPayoutEventMap: Record<HoldMotionQueueType, PayoutEvent> = Object.fromEntries(
  Object.entries(HoldMotionQueueConfig).map(([queueType, config]) => [queueType, config.payoutEvent]),
) as Record<HoldMotionQueueType, PayoutEvent>;

export const HoldMotionDependentReviewQueueRoutes: ReviewQueueRoute[] = Object.values(HoldMotionQueueConfig).map(
  (config) => config.route,
);
